/* eslint-disable react-refresh/only-export-components */

import {
  createContext,
  useContext as _useContext,
  FC,
  PropsWithChildren,
  useReducer,
  useMemo,
} from 'react';

import Cookie from 'js-cookie';
import SessionExpiredModal from 'modules/account/settings/components/SessionExpiredModal';
import { createPortal } from 'react-dom';
import { toastError } from 'ui/partials/Toast';
import { getFirstMessage } from 'utils/helpers';
import { createRoot } from 'react-dom/client';

import FeatureFlag from './FeatureFlag';
import RolePermission from './RolePermission';
import Profile from './Profile';
import Tour from './Tour';
import Subscription from './Subscription';
import TimezoneProvider from './timezone.provider';

type ERROR = {
  status: 400 | 401 | 403 | 404 | 422 | 429 | 500;
  data?: {
    message?: string | string[];
  };
  [key: string]: any;
};

interface DISPATCH_ERROR {
  [key: keyof ERROR]: any;
}

interface STATE {
  isOpenBanner: boolean;
  isOpenHelp: boolean;
  isAuthenticated: boolean;
  isEmailAddressVerified: boolean;
  onError: (err: ERROR) => any;
  collapse: boolean;
}

const initialState: STATE = {
  isOpenBanner: false,
  isOpenHelp: false,
  isAuthenticated: Cookie.get('_token') !== undefined,
  isEmailAddressVerified: Cookie.get('_isEmailAddressVerified') === 'true',
  collapse: true,
  onError: (err: ERROR) => {
    try {
      const dispatchError: DISPATCH_ERROR = {
        400: () => {
          const message = err?.data?.message;
          toastError(message as string);
        },
        401: () => {
          // const message = err?.data?.message;
          // toastError(message);
          // history.push({ pathname: '/logout' });
          // setInterval(() => history.go(0), 2000);
        },
        404: () => {
          const message = err?.data?.message;
          toastError(message as string);
        },
        403: () => {
          // display toast then redirect

          const modalRoot = document.getElementById('modal-root');
          const modal = document.querySelector('#modal-root > div');
          if (modalRoot && !modal && window.location.pathname !== '/logout') {
            const root = createRoot(modalRoot);
            root.render(
              createPortal(
                <div
                  className="w-full backdrop-blur-[2px]  h-full flex items-center justify-center"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: 999,
                  }}
                >
                  <SessionExpiredModal />
                </div>,
                modalRoot,
              ),
            );
          }
        },
        422: () => {
          const messages = err?.data?.message;
          if (typeof messages === 'string') {
            toastError(messages);
            return;
          }
          toastError(getFirstMessage(messages as string[]));
        },
        429: () => {
          toastError('Too many attempts!');
        },
        500: () => {
          const message = err?.data?.message as string;

          toastError(message ?? 'Unable to communicate with server');
        },
      };

      return dispatchError[err.status]
        ? dispatchError[err.status]()
        : toastError('Unable to Communicate to server');
    } catch (error) {
      return toastError('Unable to Communicate to server');
    }
  },
};

const TYPE = [
  'toggleAuthenticated',
  'toggleBanner',
  'toggleSidebar',
  'toggleHelp',
  'toggleEmailAddressVerified',
] as const;
interface DISPATCH_REDUCER {
  type: (typeof TYPE)[number];
  payload?: any;
}

const Reducer = (state: STATE, { type, payload }: DISPATCH_REDUCER): STATE => {
  if (type === 'toggleAuthenticated') {
    return { ...state, isAuthenticated: payload };
  }

  if (type === 'toggleBanner') {
    return { ...state, isOpenBanner: payload };
  }

  if (type === 'toggleSidebar') {
    return { ...state, collapse: !state.collapse };
  }

  if (type === 'toggleHelp') {
    return { ...state, isOpenHelp: !state.isOpenHelp };
  }

  if (type === 'toggleEmailAddressVerified') {
    return { ...state, isEmailAddressVerified: payload };
  }

  return state;
};

interface CONTEXT {
  state: STATE;
  dispatch?: React.Dispatch<DISPATCH_REDUCER>;
}

export const Context = createContext({});
export const useProvider = () => _useContext(Context) as CONTEXT;

const CContext: FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(Reducer, initialState);

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <Context.Provider value={value}>
      <Tour>
        <TimezoneProvider>
          {/* <Timezone> */}
          <RolePermission>
            <Subscription>
              <FeatureFlag>
                <Profile>{children}</Profile>
              </FeatureFlag>
            </Subscription>
          </RolePermission>
          {/* </Timezone> */}
        </TimezoneProvider>
      </Tour>
    </Context.Provider>
  );
};

export default CContext;
